import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const daysUntil = (d) => {
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(d + "T00:00:00") - today) / 86400000);
};

export const expStatus = (d, expDays) => {
  const days = daysUntil(d);
  if (days === null) return null;
  if (days < 0) return { key: "expired", label: "Expired", cls: "text-rose-600 border-rose-300 bg-rose-50 dark:bg-rose-950/30" };
  if (days <= expDays) return { key: "soon", label: days === 0 ? "Expires today" : `${days}d left`, cls: "text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/30" };
  return { key: "ok", label: "OK", cls: "text-emerald-600 border-emerald-200" };
};

export const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");

const BatchRow = ({ b, showKiosk, expDays, onChanged, tid }) => {
  const [qty, setQty] = useState(b.qty);
  const [date, setDate] = useState(b.best_before);
  const s = expStatus(date, expDays);
  const save = async (patch) => {
    try { await api.put(`/stock/batches/${b.id}`, patch); toast.success("Batch updated"); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async () => {
    try { await api.delete(`/stock/batches/${b.id}`); toast.success("Batch removed"); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`batch-row-${tid}`}>
      {showKiosk && <span className="text-xs text-slate-500 w-32 truncate" title={b.kiosk_name}>{b.kiosk_name}</span>}
      <Input type="number" min={1} className="h-8 w-20 text-right" value={qty} data-testid={`batch-qty-${tid}`}
        onChange={(e) => setQty(e.target.value)}
        onBlur={() => { if (Number(qty) !== b.qty && Number(qty) >= 1) save({ qty: Number(qty) }); }} />
      <span className="text-xs text-slate-400">units · best before</span>
      <Input type="date" className="h-8 w-40" value={date || ""} data-testid={`batch-date-${tid}`}
        onChange={(e) => setDate(e.target.value)}
        onBlur={() => { if (date && date !== b.best_before) save({ best_before: date }); }} />
      {s && <Badge variant="outline" className={s.cls} data-testid={`batch-badge-${tid}`}>{s.label}</Badge>}
      <Button variant="ghost" size="sm" className="h-8 px-2 text-rose-600" onClick={remove} data-testid={`batch-delete-${tid}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
};

const AddBatch = ({ row, kiosks, kioskId, onChanged, tid }) => {
  const [form, setForm] = useState({ kiosk_id: kioskId || (kiosks.length === 1 ? kiosks[0].id : ""), qty: row.undated_units || "", best_before: "" });
  const add = async () => {
    if (!form.kiosk_id || !form.best_before || Number(form.qty) < 1) { toast.error("Choose a kiosk, quantity and best-before date"); return; }
    try {
      await api.post("/stock/batches", { kiosk_id: form.kiosk_id, product_id: row.product_id, qty: Number(form.qty), best_before: form.best_before });
      toast.success("Batch added");
      setForm({ ...form, qty: "", best_before: "" });
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-dashed border-slate-200 dark:border-slate-800">
      {!kioskId && kiosks.length > 1 && (
        <Select value={form.kiosk_id} onValueChange={(v) => setForm({ ...form, kiosk_id: v })}>
          <SelectTrigger className="h-8 w-32" data-testid={`batch-add-kiosk-${tid}`}><SelectValue placeholder="Kiosk" /></SelectTrigger>
          <SelectContent>{kiosks.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <Input type="number" min={1} placeholder="Qty" className="h-8 w-20 text-right" value={form.qty} data-testid={`batch-add-qty-${tid}`} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
      <span className="text-xs text-slate-400">units · best before</span>
      <Input type="date" className="h-8 w-40" value={form.best_before} data-testid={`batch-add-date-${tid}`} onChange={(e) => setForm({ ...form, best_before: e.target.value })} />
      <Button size="sm" variant="outline" className="h-8" onClick={add} data-testid={`batch-add-save-${tid}`}><Plus className="h-4 w-4 mr-1" /> Add batch</Button>
    </div>
  );
};

export const BatchPanel = ({ row, kiosks, kioskId, expDays, onChanged }) => {
  const tid = row.sku || row.product_id;
  return (
    <div className="space-y-2 py-2 pl-2" data-testid={`batch-panel-${tid}`}>
      {row.undated_units > 0 && (
        <p className="text-xs text-amber-600 flex items-center gap-1" data-testid={`batch-missing-${tid}`}><AlertTriangle className="h-3.5 w-3.5" /> Dates missing for {row.undated_units} unit{row.undated_units === 1 ? "" : "s"}</p>
      )}
      {row.over_dated_units > 0 && (
        <p className="text-xs text-rose-600 flex items-center gap-1" data-testid={`batch-over-${tid}`}><AlertTriangle className="h-3.5 w-3.5" /> Batches total {row.over_dated_units} more than the unit count — check and correct</p>
      )}
      {!row.batches.length && <p className="text-xs text-slate-400">No dated batches yet. Sales take units from the shortest-dated batch first.</p>}
      {row.batches.map((b, i) => (
        <BatchRow key={`${b.id}-${b.qty}-${b.best_before}`} b={b} showKiosk={!kioskId} expDays={expDays} onChanged={onChanged} tid={`${tid}-${i}`} />
      ))}
      <AddBatch row={row} kiosks={kiosks} kioskId={kioskId} onChanged={onChanged} tid={tid} />
    </div>
  );
};
